import React, { useState, useEffect } from "react";
import config from "../../Config";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";

const StudentPaymentReportPrint = () => {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Student payment report";

    const urlParams = new URLSearchParams(window.location.search);
    const studentId = parseInt(urlParams.get("Student_ID") || "0", 10);
    const startDate = urlParams.get("StartDate");
    const endDate = urlParams.get("EndDate");

    if (studentId <= 0 || !startDate || !endDate) {
      setErrorMessage("Invalid report link. Use Student payment report to generate again.");
      setLoading(false);
      return;
    }

    axios({
      method: "post",
      url: config.base_url + "Student/GetStudentPaymentReport",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        Student_ID: studentId,
        StartDate: startDate,
        EndDate: endDate,
      },
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          setPayments(res.data.ListofPayments || []);
          setMeta(res.data);
        } else {
          setErrorMessage(
            res.data?.status_message || "Unable to load payment report."
          );
        }
        setLoading(false);
      })
      .catch(() => {
        setErrorMessage("Network error loading payment report.");
        setLoading(false);
      });
  }, [AccessKey, UserID, MemberType]);

  const totalAmount =
    meta?.TotalPaid ??
    payments.reduce((s, p) => s + Number(p.Amount || 0), 0);

  return (
    <Page orientation="portrait" size="a4">
      <PrintHeader title="Student Payment Report" />
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : errorMessage ? (
        <div className="alert alert-warning m-4">{errorMessage}</div>
      ) : (
        <div className="p-3">
          <table className="table table-sm table-bordered mb-3" style={{ fontSize: "12px" }}>
            <tbody>
              <tr>
                <th style={{ width: "28%" }}>Student</th>
                <td>
                  {meta?.Student_Name} (Reg {meta?.Registration_No})
                </td>
              </tr>
              <tr>
                <th>Father name</th>
                <td>{meta?.Father_Name}</td>
              </tr>
              <tr>
                <th>Class / Section</th>
                <td>
                  {meta?.Class_Name}
                  {meta?.Section_Name ? ` / ${meta.Section_Name}` : ""}
                </td>
              </tr>
              <tr>
                <th>Report period</th>
                <td>
                  {moment(meta?.DateFrom).format("DD MMM YYYY")} —{" "}
                  {moment(meta?.DateTo).format("DD MMM YYYY")}
                </td>
              </tr>
              <tr>
                <th>Total amount paid</th>
                <td>
                  <strong>
                    Rs.{" "}
                    {Number(totalAmount).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>

          <p className="small text-muted">
            Legacy ledger (before Apr 2026): {meta?.LegacyPaymentCount ?? 0} payment(s).
            Paid challans (Apr 2026 onward): {meta?.ChallanPaymentCount ?? 0} payment(s).
          </p>

          <table className="table table-sm table-bordered table-print" style={{ fontSize: "11px" }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Source</th>
                <th>Receipt / Challan</th>
                <th>Description</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No payments in this period.
                  </td>
                </tr>
              ) : (
                payments.map((row, idx) => (
                  <tr key={`${row.Source}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{moment(row.Payment_Date).format("DD/MM/YYYY")}</td>
                    <td>{row.Source}</td>
                    <td>{row.Reference_No || "—"}</td>
                    <td>
                      {row.Description}
                      {row.Bank_Scroll_No
                        ? ` (Scroll ${row.Bank_Scroll_No})`
                        : ""}
                    </td>
                    <td className="text-end">
                      {Number(row.Amount).toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {payments.length > 0 && (
              <tfoot>
                <tr>
                  <th colSpan={5} className="text-end">
                    Total
                  </th>
                  <th className="text-end">
                    {Number(totalAmount).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                  </th>
                </tr>
              </tfoot>
            )}
          </table>

          <p className="small mt-4 mb-0" style={{ fontSize: "10px" }}>
            This report lists fee payments recorded in the school system for the
            period shown. Payments before April 2026 are from the historical
            student ledger; later payments are from paid fee challans. Issued
            for parent / official use only.
          </p>
          <p className="small text-muted mb-0" style={{ fontSize: "9px" }}>
            Generated on {moment().format("DD MMM YYYY HH:mm")}
          </p>
        </div>
      )}
    </Page>
  );
};

export default StudentPaymentReportPrint;
